/**
 * subject-summary.component.ts
 * Copyright © 2024, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { UserService } from "../auth/identity/user.service";
import { SharedList, WatchlistService } from "./watchlist.service";
import { FormControl } from "@angular/forms";
import { Subscription } from "rxjs";
import { ModalService } from "app/common/modal.service";
import { CreateSharedListModalComponent, SharedListModalResult } from "./create-shared-list-modal.component";
import { WatchlistResponse } from "app/server/messages/watchlists";
import { jsonify } from "app/support/utilities";
import { Rule } from "app/auth/permissions/rules";


/**
 * The 
 */
@Component({
  template: `
  <div class="container">
		<!-- <div *ngIf="users.debugMode" class="jumbotron">
			Known List Opaque IDs
			<ul>
				<li>obj:{{jsonify(worklists.personalWorklistObjectId)}} → PERSONAL</li>
				<li *ngFor="let pair of worklists.objectIdToGroupId.entries()">obj:{{pair[0]}} → grp:{{pair[1]}}</li>
			</ul>
		</div> -->
    <h3>
      Available Worklists <span class="badge">{{currentLists.length}}</span>
			<small class="form-inline pull-right">
				<label for="group">in group:</label>
				<select id="group" class="form-control" [formControl]="groupControl" [disabled]="users.self.sharedListGroups.length <= 1">
					<option
						*ngFor="let gp of users.self.sharedListGroups"
						[ngValue]="gp.id"
					>
						{{ gp.name }}
					</option>
				</select>
				<span
					class="badge"
					title="This is the number of groups you can access lists for"
					style="position: relative; left: -12px; top: -16px;">
					{{ users.self.sharedListGroups.length }}
				</span>
			</small>
    </h3>
		<table class="table">
			<thead>
				<tr>
					<th>Visibility</th>
					<th [sorts]="currentLists" asType="string" dot="title" [runOnChange]="true">Title</th>
					<th [sorts]="currentLists" asType="string" dot="description">Description</th>
					<th [sorts]="currentLists" dot="created">Created</th>
					<th>Actions</th>
				</tr>
			</thead>
				<tbody *ngIf="!loadedForGroup">
					<tr >
						<td colspan="5">
							<h4 class="text-info text-center">Loading...</h4>
						</td>
					</tr>
				</tbody>
				<tbody *ngIf="loadedForGroup">
					<tr *ngFor="let list of currentLists">
						<td><span class="badge" title="A 'group-public' worklist is potentially visible to all users in the selected group.">group-public</span></td>
						<td>{{ list.title }}</td>
						<td>{{ list.description }}</td>
						<td>{{ list.created | utcDate : "MMM D, YYYY [at] h:mma" }}
							<!-- <span style="opacity: 0.5">{{list.created}}</span> -->
						</td>
						<td style="padding: 2px">
							<button
								class="btn btn-primary w100"
								(click)="openList(list.objectId)">
								Open List
							</button>
						</td>
					</tr>
					<tr *ngIf="currentLists.length < 1">
						<td colspan="5">
							<h4 class="text-muted text-center">This group has no lists yet!</h4>
						</td>
					</tr>
					<!-- <tr *ngIf="currentLists.length < 1">
						<td colspan="5" class="text-center">
							<button class="btn btn-success" style="padding: auto 2em" (click)="createViaDialog()">
								Create New Shared List
							</button>
						</td>
					</tr> -->
				</tbody>
		</table>
		<div class="text-center" style="margin-top: 2em auto;">
			<button
				class="btn btn-success" 
				[attr.disabled]="canCreateShared() ? null : ''"
				(click)="createViaDialog()">
				Create New Shared Worklist...
			</button>
		</div>
	</div>
  `,
  styles: [
  ]
})
export class WorklistBrowserComponent implements OnInit, OnDestroy
{
	public groupControl : FormControl = new FormControl(undefined);

	public loadedForGroup : boolean = false;
	public currentLists : SharedList[] = [];
	public newListOpen : boolean = false;

	public jsonify = jsonify;

	private batchSubscription : Subscription = new Subscription();

  constructor (
    protected worklists : WatchlistService,
    protected router : Router,
    protected route : ActivatedRoute,
    protected users : UserService,
		protected modal : ModalService
  ) {
		if (this.users.self.sharedListGroups[0]) {
			this.groupControl.setValue(this.users.self.sharedListGroups[0].id);
		}
	}

	private loadFor (gid : number, force : boolean = false) : void
	{
		if (this.worklists.sharedListsByGroup.has(gid) && !force) {
			this.loadedForGroup = true;
			this.currentLists = this.worklists.sharedListsByGroup.get(gid);
		} else {
			this.loadedForGroup = false;
			this.worklists.requestListsForGroup(gid).subscribe(_ => {
				this.loadedForGroup = true;
				this.currentLists = this.worklists.sharedListsByGroup.get(gid);
			});
		}
	}

  public ngOnInit () : void
  {
		this.batchSubscription.add(
			this.groupControl.valueChanges.subscribe(gid => {
				this.loadFor(gid)
			})
		);
		if (Number.isSafeInteger(this.groupControl.value)) {
			this.loadFor(this.groupControl.value);
		} else if (this.groupControl.value === undefined 
			&& this.users.self.sharedListGroups[0]) {
			// groups weren't available at construction time but are now!
			// just set it because we already did the subscriber above
			this.groupControl.setValue(this.users.self.sharedListGroups[0].id);
		} else {
			console.warn(`Current group id (${this.groupControl.value}) is not valid, suppressing auto-request until new value provided`);
		}
  }

	public ngOnDestroy(): void {
		this.batchSubscription.unsubscribe();
	}

	public openList (listId : number) {
		if (!Number.isSafeInteger(listId)) {
			return console.error(
				`Cannot request a list without opaque ID, got: ${listId}`);
		}
		this.worklists.loadSharedList(this.groupControl.value, listId)
			.subscribe(list => this.router.navigate(
				["lists/byGroup", this.groupControl.value, listId]
			));
	}

	public createViaDialog () {
		this.modal.observe<SharedListModalResult>(
      CreateSharedListModalComponent,
      {size: "md", groupId: this.groupControl.value}
    ).subscribe((r : SharedListModalResult | null) => {
      if (r)
      {
        this.worklists.createSharedList(r).subscribe(
					(rsp : SharedList) => {
						// debugger;
					});
      }
    });
	}

	public canCreateShared () : boolean {
		return this.users.self.sharedListGroups.some(
			g => g.id === this.groupControl.value && g.rules.includes(Rule.CREATE_SHARED_WORKLIST));
	}
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/watchlist/lists-browser.component.ts